import Image from 'next/image';
import Link from 'next/link';

const APP_STORE_URL = 'https://apps.apple.com/us/app/sola-bible-app/id6739747733';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.sola.bible';

interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
}

const columns: { heading: string; links: FooterLink[] }[] = [
  {
    heading: 'Product',
    links: [
      { label: 'Explain a verse', href: '/bible-verse-explained' },
      { label: 'Blog', href: '/blog' },
      { label: 'Hebrew words', href: '/hebrew-words' },
      { label: 'Greek words', href: '/greek-words' },
      { label: 'About', href: '/about' },
    ],
  },
  {
    heading: 'Get Sola',
    links: [
      { label: 'Download for iOS', href: APP_STORE_URL, external: true },
      { label: 'Download for Android', href: PLAY_STORE_URL, external: true },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms of Service', href: '/terms' },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="relative border-t border-border">
      {/* Gilded top edge — the gold hairline closing the page */}
      <div
        aria-hidden="true"
        className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[rgba(212,163,62,0.55)] to-transparent"
      />
      <div className="container py-14 sm:py-16">
        <div className="grid grid-cols-2 gap-y-10 gap-x-6 sm:grid-cols-[1.3fr_1fr_1fr_1fr] sm:gap-x-10">
          <div className="col-span-2 sm:col-span-1">
            <Image
              src="/sola_logo_basic_white.png"
              alt="Sola"
              width={120}
              height={40}
              className="h-6 w-auto mb-4"
            />
            <p className="text-sm text-muted-foreground max-w-[26ch] leading-relaxed">
              Scripture stays the center. Sola is a study companion, not a substitute.
            </p>
          </div>

          {columns.map((col) => (
            <div key={col.heading}>
              <p className="rubric mb-4">
                {col.heading}
              </p>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-foreground/80 hover:text-foreground transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        href={link.href}
                        className="text-sm text-foreground/80 hover:text-foreground transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          {/* The scribe's last line — fol. ult. = the final folio, the joke */}
          <p className="folio">
            &copy; {new Date().getFullYear()} Sola Bible App &middot; fol. ult.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://www.instagram.com/solabibleapp"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Instagram
            </a>
            <a
              href="https://www.tiktok.com/@sola.amy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              TikTok
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
